import type { SortingDashboardDrillType } from './components/SortingDashboardDetailDrawer';
import type { SortingDashboardBusinessTypeTab } from './sortingDashboardOrderFlowConstants';
import {
    NEXT_DAY_HANDOVER_RATE_TOOLTIP,
    FINAL_NOT_HANDOVER_RATE_TOOLTIP,
    OVERALL_MISS_SCAN_RATE_TOOLTIP,
} from './sortingDashboardOrderFlowConstants';

/** 分拣看板配置（断更看板已迁移至 NoUpdateDashboardPage） */
export type SortingDashboardVariant = 'sorting';

/** 主展开区指标行（入库包裹量之下的顶级指标） */
export interface SortingTopLevelMetricConfig {
    key: string;
    label: string;
    field: string;
    isRate: boolean;
    tooltip?: string;
    isGroup?: boolean;
}

/** 分组指标下的子行（错分展开） */
export interface SortingMetricChildRowConfig {
    key: string;
    label: string;
    field: string;
    isRate: boolean;
    tooltip?: string;
}

/** 副指标行抽屉标题前缀（与主指标区分），key 与表格 metricType 一致 */
export const CHILD_DRILL_GROUP_TITLE: Partial<Record<string, string>> = {
    wrongSortToWrongLineABCount: '错分率',
    wrongSortToWrongHubACount: '错分率',
    wrongSortToWrongWhsBCount: '错分率',
    wrongSortToWrongLineCCount: '错分率',
    ascanMissRate: '入库漏扫率',
    transferArrivalMissRate: '入库漏扫率',
    notCorrectlyPackedCount: '最终未交接包裹数',
    bagLabelNotShippedCount: '最终未交接包裹数',
    noDepartureActionCount: '最终未交接包裹数',
};

/** 尾程仓 tab 允许下钻的指标类型（与 SortingDashboardDetailDrawer 一致） */
export const DRILL_METRIC_TYPES_LAST_MILE_HUB: SortingDashboardDrillType[] = [
    'crossBorderOnline24hRate',
    'shouldArriveNotArriveRate',
    'outbound12hRate',
    'overallMissScanRate',
    'ascanMissRate',
    'transferArrivalMissRate',
    'outboundMissRate',
    'wrongSortRate',
    'wrongSortToWrongLineABCount',
    'wrongSortToWrongHubACount',
    'wrongSortToWrongWhsBCount',
    'wrongSortToWrongLineCCount',
];

/** 分拣仓 tab 允许下钻的指标类型 */
export const DRILL_METRIC_TYPES_SORTING_HUB: SortingDashboardDrillType[] = [
    'nextDayHandoverRate',
    'fbtSameDayHandoverRate',
    'cbtNextDayHandoverRate',
    'shouldArriveNotArriveRate',
    'shouldArriveNotArriveCount',
    'notCorrectlyPackedCount',
    'bagLabelNotShippedCount',
    'noDepartureActionCount',
];

const NEXT_DAY_HANDOVER_ROW: SortingTopLevelMetricConfig = {
    key: 'nextDayHandoverRate',
    label: '次日交接率',
    field: 'nextDayHandoverRate',
    isRate: true,
    tooltip: NEXT_DAY_HANDOVER_RATE_TOOLTIP,
};

const FINAL_NOT_HANDOVER_ROW: SortingTopLevelMetricConfig = {
    key: 'shouldArriveNotArriveRate',
    label: '最终未交接率',
    field: 'shouldArriveNotArriveRate',
    isRate: true,
    tooltip: FINAL_NOT_HANDOVER_RATE_TOOLTIP,
};

const FINAL_NOT_HANDOVER_COUNT_ROW: SortingTopLevelMetricConfig = {
    key: 'shouldArriveNotArriveCount',
    label: '最终未交接包裹数',
    field: 'shouldArriveNotArriveCount',
    isRate: false,
    isGroup: true,
};

/** 尾程仓 tab 顶级指标：不含次日交接率（分拣仓专属，尾程仓前端隐藏） */
export const TOP_LEVEL_METRICS_LAST_MILE_HUB: SortingTopLevelMetricConfig[] = [
    { key: 'crossBorderOnline24hRate', label: '跨境客户24小时上网率', field: 'crossBorderOnline24hRate', isRate: true, tooltip: '跨境客户交接包裹中24小时内小包上网包裹的比率；交接以清关交付时间为准，无清关交付时间用大包扫描时间' },
    { key: 'outbound12hRate', label: '12小时出库率', field: 'outbound12hRate', isRate: true, tooltip: '入库包裹中，入库后12小时内出库的包裹率' },
    FINAL_NOT_HANDOVER_ROW,
    { key: 'overallMissScanRate', label: '入库漏扫率', field: 'overallMissScanRate', isRate: true, isGroup: true, tooltip: OVERALL_MISS_SCAN_RATE_TOOLTIP },
    { key: 'outboundMissRate', label: '出库漏扫率', field: 'outboundMissRate', isRate: true, tooltip: '入库包裹中，截止目前无出库扫描且有后续轨迹的包裹率' },
    { key: 'wrongSortRate', label: '错分率', field: 'wrongSortRate', isRate: true, isGroup: true, tooltip: '入库后，被分到错误目的地的包裹率' },
    { key: 'sortEfficiency', label: '分拣时效', field: 'sortEfficiency', isRate: false, tooltip: '当日平均每小时分拣包裹量' },
    { key: 'sortPeak', label: '分拣峰值', field: 'sortPeak', isRate: false, tooltip: '当日峰值小时分拣包裹量' },
];

/** 分拣仓 tab 顶级指标：次日交接率 / 最终未交接率 / 最终未交接包裹数（后者可展开 3 种错误类型子行） */
export const TOP_LEVEL_METRICS_SORTING_HUB: SortingTopLevelMetricConfig[] = [
    NEXT_DAY_HANDOVER_ROW,
    FINAL_NOT_HANDOVER_ROW,
    FINAL_NOT_HANDOVER_COUNT_ROW,
];

export const WRONG_SORT_CHILD_CONFIGS: SortingMetricChildRowConfig[] = [
    { key: 'wrongSortToWrongLineABCount', label: '错分至错误路线 (A+B)', field: 'wrongSortToWrongLineABCount', isRate: true, tooltip: '入库后，被分到错误HUB(A)或同HUB下错误WHS(B)的包裹率' },
    { key: 'wrongSortToWrongHubACount', label: '错分至错误HUB (A)', field: 'wrongSortToWrongHubACount', isRate: true, tooltip: '入库后，被分到错误HUB的包裹率' },
    { key: 'wrongSortToWrongWhsBCount', label: '错分至错误WHS (B)', field: 'wrongSortToWrongWhsBCount', isRate: true, tooltip: '入库后，被分到同HUB下错误WHS的包裹率' },
    { key: 'wrongSortToWrongLineCCount', label: '错分至错误线路 (C)', field: 'wrongSortToWrongLineCCount', isRate: true, tooltip: '入库后，被分到同HUB下错误线路的包裹率' },
];

export const FINAL_NOT_HANDOVER_CHILD_CONFIGS: SortingMetricChildRowConfig[] = [
    { key: 'notCorrectlyPackedCount', label: '未正确装包 (错分)', field: 'notCorrectlyPackedCount', isRate: false },
    { key: 'bagLabelNotShippedCount', label: '包牌未发运 (漏Departure）', field: 'bagLabelNotShippedCount', isRate: false },
    { key: 'noDepartureActionCount', label: '没有发车动作 (漏Hub Departed）', field: 'noDepartureActionCount', isRate: false },
];

export const OVERALL_MISS_SCAN_CHILD_CONFIGS: SortingMetricChildRowConfig[] = [
    { key: 'ascanMissRate', label: 'ASCAN漏扫率', field: 'ascanMissRate', isRate: true, tooltip: '司机扫描或手动领取的包裹中，缺少揽收入库扫描的包裹率，按司机首次扫描或手动领取的日期归属' },
    { key: 'transferArrivalMissRate', label: '转运到仓漏扫率', field: 'transferArrivalMissRate', isRate: true, tooltip: '司机扫描或手动领取的包裹中，且揽收仓 <> 目的地仓的包裹中，缺少目的地仓入库扫描的包裹率，按司机首次扫描或手动领取的日期归属' },
];

export interface SortingDashboardVariantTablesConfig {
    drillMetricTypes: SortingDashboardDrillType[];
    topLevelMetricConfigs: SortingTopLevelMetricConfig[];
    wrongSortChildConfigs: SortingMetricChildRowConfig[];
    overallMissScanChildConfigs: SortingMetricChildRowConfig[];
    finalNotHandoverChildConfigs: SortingMetricChildRowConfig[];
}

export function getSortingDashboardVariantConfig(
    businessTypeTab: SortingDashboardBusinessTypeTab = 'lastMileHub'
): SortingDashboardVariantTablesConfig {
    if (businessTypeTab === 'sortingHub') {
        return {
            drillMetricTypes: DRILL_METRIC_TYPES_SORTING_HUB,
            topLevelMetricConfigs: TOP_LEVEL_METRICS_SORTING_HUB,
            wrongSortChildConfigs: [],
            overallMissScanChildConfigs: [],
            finalNotHandoverChildConfigs: FINAL_NOT_HANDOVER_CHILD_CONFIGS,
        };
    }
    return {
        drillMetricTypes: DRILL_METRIC_TYPES_LAST_MILE_HUB,
        topLevelMetricConfigs: TOP_LEVEL_METRICS_LAST_MILE_HUB,
        wrongSortChildConfigs: WRONG_SORT_CHILD_CONFIGS,
        overallMissScanChildConfigs: OVERALL_MISS_SCAN_CHILD_CONFIGS,
        finalNotHandoverChildConfigs: [],
    };
}
